import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { communities } from '@/mocks/communities';
import GlowButton from '@/components/base/GlowButton';
import ParticleCanvas from './ParticleCanvas';

const featured = communities.filter((c) => c.featured);

function enterStyle(delay: number, visible: boolean): React.CSSProperties {
  return {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(20px)',
    transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms`,
  };
}

export default function HeroFeaturedBanner() {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'left' | 'right'>('right');
  const [entered, setEntered] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 80);
    return () => clearTimeout(t);
  }, []);

  const goTo = useCallback((index: number, dir: 'left' | 'right' = 'right') => {
    if (isAnimating) return;
    setDirection(dir);
    setIsAnimating(true);
    setTimeout(() => {
      setCurrent(index);
      setIsAnimating(false);
    }, 350);
  }, [isAnimating]);

  const next = useCallback(() => {
    goTo((current + 1) % featured.length, 'right');
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + featured.length) % featured.length, 'left');
  }, [current, goTo]);

  useEffect(() => {
    timerRef.current = setInterval(next, 5000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [next]);

  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(next, 5000);
  };

  const handlePrev = () => { resetTimer(); prev(); };
  const handleNext = () => { resetTimer(); next(); };
  const handleDot = (i: number) => {
    resetTimer();
    goTo(i, i > current ? 'right' : 'left');
  };

  // 触摸滑动手势
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    // 水平滑动距离 > 40px 且水平方向为主
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) { resetTimer(); next(); }
      else { resetTimer(); prev(); }
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const navigate = useNavigate();
  const formatCount = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : n.toString();
  const c = featured[current];

  return (
    <section
      className="w-full relative overflow-hidden"
      style={{ minHeight: '220px', height: 'clamp(220px, 42vw, 340px)' }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Particle layer */}
      <ParticleCanvas count={52} color="0,136,204" className="z-[2]" />

      {/* Background slides */}
      {featured.map((item, i) => (
        <div
          key={item.id}
          className="absolute inset-0 transition-opacity duration-500"
          style={{ opacity: i === current ? 1 : 0, zIndex: i === current ? 1 : 0 }}
        >
          <img src={item.coverImage} alt={item.name} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(105deg, rgba(232,244,252,0.82) 0%, rgba(220,238,250,0.60) 45%, rgba(200,228,248,0.22) 100%)',
          }}></div>
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(to top, rgba(232,240,247,0.70) 0%, transparent 55%)',
          }}></div>
          <div className="absolute top-0 right-0 w-1/2 h-full" style={{
            background: 'radial-gradient(ellipse at 80% 40%, rgba(0,168,240,0.12) 0%, transparent 65%)',
          }}></div>
          <div className="absolute bottom-0 left-1/4 w-1/2 h-1/2" style={{
            background: 'radial-gradient(ellipse at 50% 100%, rgba(100,200,255,0.10) 0%, transparent 70%)',
          }}></div>
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 h-full max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 flex items-center">
        <div
          className="w-full max-w-xl"
          style={{
            opacity: isAnimating ? 0 : 1,
            transform: isAnimating
              ? `translateX(${direction === 'right' ? '-24px' : '24px'})`
              : 'translateX(0)',
            transition: 'opacity 0.35s ease, transform 0.35s ease',
          }}
        >
          <div className="flex items-center gap-2 mb-2 md:mb-3" style={enterStyle(0, entered)}>
            <span className="inline-flex items-center gap-1.5 text-[10px] md:text-[11px] font-bold text-[#0088CC] bg-[#0088CC]/12 border border-[#0088CC]/25 px-2.5 md:px-3 py-0.5 md:py-1 rounded-full uppercase tracking-wider backdrop-blur-sm">
              <i className="ri-star-fill text-[9px] md:text-[10px]"></i>
              Featured
            </span>
            <span className="text-[10px] md:text-[11px] text-slate-500 bg-white/70 border border-slate-200/60 px-2 md:px-2.5 py-0.5 md:py-1 rounded-full backdrop-blur-sm">
              {c.category}
            </span>
          </div>

          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-slate-800 leading-tight mb-1.5 md:mb-2 tracking-tight line-clamp-2" style={enterStyle(100, entered)}>
            {c.name}
          </h2>

          <p className="hidden sm:block text-slate-600 text-sm md:text-base leading-relaxed mb-3 md:mb-4 line-clamp-2" style={enterStyle(180, entered)}>
            {c.description}
          </p>

          <div className="hidden sm:flex items-center gap-3 md:gap-4 mb-3 md:mb-5 flex-wrap" style={enterStyle(250, entered)}>
            <div className="flex items-center gap-1.5 text-sm">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-group-line text-[#0088CC]"></i>
              </div>
              <span className="font-semibold text-slate-800">{formatCount(c.memberCount)}</span>
              <span className="text-slate-400">members</span>
            </div>
            <div className="w-px h-4 bg-slate-300/60"></div>
            <div className="flex items-center gap-1.5 text-sm">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-map-pin-line text-[#0088CC]"></i>
              </div>
              <span className="text-slate-500">{c.region}</span>
            </div>
            <div className="w-px h-4 bg-slate-300/60"></div>
            <div className="flex items-center gap-1.5 text-sm">
              <span className={`inline-block w-2 h-2 rounded-full ${
                c.activityLevel === 'high' ? 'bg-emerald-500' : c.activityLevel === 'medium' ? 'bg-amber-500' : 'bg-slate-400'
              }`}></span>
              <span className="text-slate-500 capitalize">{c.activityLevel} activity</span>
            </div>
          </div>

          <div className="hidden md:flex flex-wrap gap-1.5 mb-4 md:mb-5" style={enterStyle(320, entered)}>
            {c.tags.map((tag) => (
              <span key={tag} className="text-[11px] text-slate-500 bg-white/70 border border-slate-200/60 px-2.5 py-0.5 rounded-full backdrop-blur-sm">
                #{tag}
              </span>
            ))}
          </div>

          <div className="flex items-center gap-2 md:gap-3 mt-2 md:mt-0" style={enterStyle(400, entered)}>
            <GlowButton
              as="a"
              href={c.telegramLink}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="text-xs sm:text-sm font-bold px-3 sm:px-5 py-2 sm:py-2.5 rounded-full"
            >
              <i className="ri-telegram-fill"></i>
              <span className="hidden xs:inline">Join Community</span>
              <span className="xs:hidden">Join</span>
            </GlowButton>
            <GlowButton
              onClick={() => navigate(`/community/${c.id}`)}
              className="text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5 rounded-full"
              variant="secondary"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-information-line"></i>
              </div>
              <span className="hidden sm:inline">View Details</span>
            </GlowButton>
          </div>
        </div>

        {/* Right side thumbnails — desktop only */}
        <div className="hidden lg:flex flex-col items-end gap-3 ml-auto" style={enterStyle(300, entered)}>
          <div className="flex gap-2">
            {featured.map((item, i) => (
              <button
                key={item.id}
                onClick={() => {
                  if (i === current) {
                    navigate(`/community/${item.id}`);
                  } else {
                    handleDot(i);
                  }
                }}
                title={i === current ? `View ${item.name} details` : `Switch to ${item.name}`}
                className={`relative overflow-hidden rounded-xl transition-all duration-300 cursor-pointer ${
                  i === current
                    ? 'w-28 h-20 ring-2 ring-[#0088CC] ring-offset-2 ring-offset-transparent'
                    : 'w-16 h-12 opacity-40 hover:opacity-70'
                }`}
              >
                <img src={item.coverImage} alt={item.name} className="w-full h-full object-cover object-top" />
                {i === current && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-1.5">
                    <span className="text-[9px] text-white font-semibold leading-tight line-clamp-1">{item.name}</span>
                  </div>
                )}
                {i === current && (
                  <div className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center bg-black/40 rounded-full">
                    <i className="ri-external-link-line text-white" style={{ fontSize: '8px' }}></i>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom control bar: prev arrow · dots · next arrow */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {/* Prev */}
        <button
          onClick={handlePrev}
          className="w-7 h-7 flex items-center justify-center bg-white/40 hover:bg-white/70 backdrop-blur-md border border-[#b8d4ea]/50 rounded-full text-slate-600 cursor-pointer transition-all"
        >
          <i className="ri-arrow-left-s-line text-base"></i>
        </button>

        {/* Dots */}
        <div className="flex items-center gap-2">
          {featured.map((_, i) => (
            <button
              key={i}
              onClick={() => handleDot(i)}
              className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                i === current ? 'w-8 bg-[#0088CC]' : 'w-2 bg-slate-400/50 hover:bg-slate-500/60'
              }`}
            />
          ))}
        </div>

        {/* Next */}
        <button
          onClick={handleNext}
          className="w-7 h-7 flex items-center justify-center bg-white/40 hover:bg-white/70 backdrop-blur-md border border-[#b8d4ea]/50 rounded-full text-slate-600 cursor-pointer transition-all"
        >
          <i className="ri-arrow-right-s-line text-base"></i>
        </button>
      </div>

      {/* Slide counter */}
      <div className="absolute bottom-4 right-4 md:right-6 z-20 text-xs text-slate-500 font-mono">
        <span className="text-slate-700 font-bold">{String(current + 1).padStart(2, '0')}</span>
        <span className="mx-1">/</span>
        <span>{String(featured.length).padStart(2, '0')}</span>
      </div>
    </section>
  );
}
